import { useState, useRef, useEffect } from "react";
import { GetOwnedProducts } from "./queries";
import {
  buildGetOwnedProductsVariables,
  fetchMoreProducts,
  buildExportProductsVariables
} from "./helpers";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { useMixpanel } from "react-mixpanel-browser";
import { ExportProducts } from "./mutations";
import currency from "currency-formatter";
import moment from "moment";

export function useProductListPagination({
  products,
  productsLoading,
  fetchMore,
  searchText
}) {
  let resizeTimeout;
  const preventRefetch = useRef(false);

  const [fetchingMore, setFetchingMore] = useState(false);

  const onScroll = async (e) => {
    if (productsLoading || preventRefetch.current || fetchingMore) return;

    const element = e.target;
    const contentNotAsTallAsWindow = window.innerHeight >= element.scrollHeight;

    const haveScrolledToEnd =
      element.scrollHeight - element.scrollTop - 300 <= element.clientHeight;

    if (
      (contentNotAsTallAsWindow || haveScrolledToEnd) &&
      products.length &&
      !preventRefetch.current
    ) {
      setFetchingMore(true);
      await fetchMoreProducts({
        fetchMore,
        searchText,
        products,
        preventRefetch
      });
      setFetchingMore(false);
    }
  };

  useEffect(() => {
    !!products &&
      onScroll({
        target: document.getElementsByTagName("main").item(0)
      });
  }, [products, fetchingMore]);

  useEffect(() => {
    const onResize = () => {
      clearTimeout(resizeTimeout);

      resizeTimeout = setTimeout(
        () =>
          !!products &&
          onScroll({
            target: document.getElementsByTagName("main").item(0)
          }),
        150
      );
    };

    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, [products, productsLoading, fetchingMore]);

  return { onScroll, fetchingMore };
}

export function useProductList({ searchText }) {
  const { data, loading, fetchMore } = useQuery(
    GetOwnedProducts,
    buildGetOwnedProductsVariables(searchText)
  );

  const products = loading
    ? []
    : data.stores.ownedProducts.map((product) => ({
        ...product,
        createdAt: moment(product.createdAt).format("MMMM D, YYYY hh:mm A"),
        imageUrl: product.images[0].url,
        price: product.isDonation
          ? "Accepts Donations"
          : product.variants.length === 0
          ? `$${currency.format(product.price, { format: "USD" })}`
          : "Variant Pricing",
        totalSales: product.sales.totalSales,
        totalRevenue: `$${currency.format(product.sales.totalRevenue, {
          format: "USD"
        })}`
      }));
  return {
    loading,
    products,
    fetchMore
  };
}

export function useProductExport({ searchText }) {
  const mixpanel = useMixpanel();
  const [createProductExport] = useMutation(ExportProducts);

  async function exportProducts() {
    try {
      const result = await createProductExport(
        buildExportProductsVariables({ searchText })
      );

      mixpanel.track("Exported Product List CSV");

      const downloadTab = window.open(result.data.exportProducts.url, "_blank");
      downloadTab.focus();
    } catch (err) {
      console.error(err);

      mixpanel.track("Export Product List Failed", {
        Reason: err.message
      });
    }
  }

  return exportProducts;
}
